/**
 * The saved views under *Activity* on a phone, as the page bar's tabs (Aaron,
 * Oct 27: "can we move the views up below the activity title like the contact
 * list view so they are tabs there").
 *
 * Tasks and All activity are the list engine, embedded; on a phone the engine
 * hands its page bar's parts to a host (`ListScreen`'s `hostBar`) instead of
 * drawing them itself, and this is the host: the tab gives `hostBar` what
 * `useActivityHostBar(on)` returns, and the page draws the tabs it was handed
 * in its own `PageBar` — the tabs exactly as the Contacts phone list's bar
 * draws them, scrolling sideways — with the count as the line under
 * *Activity*, in the engine's own words ("8 of 27", the Contacts bar's), said
 * through `useActivitySub` like every other tab's line. A view changed and not
 * saved says so on that line too, in the engine's words (*8 of 27 · Edited ·
 * not saved*): the Contacts bar puts them beside its title, which on Activity's
 * bar is the view button's, the faces' and **+**'s room.
 *
 * The engine hands its bar over after every render; the page keeps what it
 * shows only when that changes, and every control it draws calls the latest
 * one, so a tab, its × and *+ Save view* always act on the list as it is now.
 * A tab that has gone takes its views with it.
 */
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { ListHostBar } from '../components/list-screen.js'
import type { PageTab } from '../frame/page-bar.js'
import { useActivitySub } from './page-sub.js'

type Hand = (speaker: string, bar: ListHostBar | null) => void

/** The engine's own words for a view changed and not saved. */
const EDITED = 'Edited · not saved'

export const ActivityViewsContext = createContext<Hand | null>(null)

/** What the page bar draws: the views as tabs. */
export interface ActivityViews {
  tabs: PageTab[]
  activeTab: string
  onTabChange: (key: string) => void
  onSaveView: () => void
}

type Handler = 'onRemove' | 'onToggleDefault' | 'onUpdate' | 'onRename'
const HANDLERS: readonly Handler[] = ['onRemove', 'onToggleDefault', 'onUpdate', 'onRename']

/** Everything about a bar that changes what is drawn — never its handlers, which are new every render. */
const shapeOf = (bar: ListHostBar): string => JSON.stringify([
  bar.tabs.map((tab) => [tab.key, tab.label, tab.isDefault ?? false, tab.count ?? null,
    HANDLERS.map((name) => Boolean(tab[name]))]),
  bar.activeTab,
])

/** The page's half: the views to draw (`null` for none), and the hand to put in `ActivityViewsContext`. */
export function useActivityViewsSlot(): { views: ActivityViews | null; hand: Hand } {
  const latest = useRef<{ speaker: string; bar: ListHostBar } | null>(null)
  const [shape, setShape] = useState('')
  const hand = useCallback<Hand>((speaker, bar) => {
    if (bar === null) {
      if (latest.current?.speaker !== speaker) return
      latest.current = null
      setShape('')
      return
    }
    latest.current = { speaker, bar }
    const next = shapeOf(bar)
    setShape((current) => (current === next ? current : next))
  }, [])
  const views = useMemo<ActivityViews | null>(() => {
    const bar = shape ? latest.current?.bar : null
    if (!bar) return null
    /* Each control calls the bar as it is when it is pressed. */
    const call = (key: string, name: Handler) => latest.current?.bar.tabs.find((tab) => tab.key === key)?.[name]?.()
    return {
      tabs: bar.tabs.map((tab) => {
        const out: PageTab = { key: tab.key, label: tab.label }
        if (tab.isDefault !== undefined) out.isDefault = tab.isDefault
        if (tab.count !== undefined) out.count = tab.count
        for (const name of HANDLERS) if (tab[name]) out[name] = () => call(tab.key, name)
        return out
      }),
      activeTab: bar.activeTab,
      onTabChange: (key) => latest.current?.bar.onTabChange(key),
      onSaveView: () => latest.current?.bar.onSaveView(),
    }
  }, [shape])
  return useMemo(() => ({ views, hand }), [views, hand])
}

/**
 * A tab's half: what to give the list engine's `hostBar` while `on` (a phone),
 * else `undefined` — which leaves the engine drawing its own chips. Its count
 * becomes the line under *Activity*. Outside the Activity page there is no
 * page bar to draw them in, so the engine keeps them.
 */
export function useActivityHostBar(on: boolean): ((bar: ListHostBar) => void) | undefined {
  const hand = useContext(ActivityViewsContext)
  const speaker = useId()
  const [sub, setSub] = useState<string | null>(null)
  const hosted = on && hand !== null
  useActivitySub(hosted ? sub : null)
  useEffect(() => {
    if (!hosted) hand?.(speaker, null)
  }, [hosted, hand, speaker])
  useEffect(() => () => hand?.(speaker, null), [hand, speaker])
  const take = useCallback((bar: ListHostBar) => {
    setSub(bar.dirty ? `${bar.sub} · ${EDITED}` : bar.sub)
    hand?.(speaker, bar)
  }, [hand, speaker])
  return hosted ? take : undefined
}
